import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import { useAlert } from '../contexts/AlertContext';
import { runAutoReschedule } from '../services/autoRescheduleService';
import { rescheduleUnschedulableByAvailability } from '../services/availabilityRescheduleService';
import './AdminPanel.css';

export default function TournamentSchedule() {
  const navigate = useNavigate();
  const { gameId: adminGameId } = useGame();
  const { showAlert } = useAlert();
  const [autoRescheduleRunning, setAutoRescheduleRunning] = useState(false);
  const [autoRescheduleResult, setAutoRescheduleResult] = useState(null);
  const [useBufferTime, setUseBufferTime] = useState(true);
  const [availabilityRescheduleRunning, setAvailabilityRescheduleRunning] = useState(false);
  const [availabilityRescheduleResult, setAvailabilityRescheduleResult] = useState(null);

  async function handleRunAutoReschedule() {
    setAutoRescheduleRunning(true);
    setAutoRescheduleResult(null);
    try {
      const data = await runAutoReschedule({ useBuffer: useBufferTime, gameId: adminGameId });
      setAutoRescheduleResult(data);
      const conflictMsg = data.conflictsResolved != null && data.conflictsResolved > 0 ? ` Resolved ${data.conflictsResolved} match(es) in conflicting slots.` : '';
      const skipMsg = data.skippedTournaments?.length ? ` Skipped ${data.skippedTournaments.length} tournament(s) missing venue or time slots.` : '';
      showAlert(`Auto-reschedule complete. Rescheduled ${data.rescheduled ?? 0} match(es) in chronological order (no double-booked slots).${conflictMsg}${skipMsg}${data.errors?.length ? ` Errors: ${data.errors.length}` : ''}`, { title: 'Auto-reschedule complete', variant: 'info' });
    } catch (err) {
      console.error('[TournamentSchedule] handleRunAutoReschedule failed', err?.message, err);
      setAutoRescheduleResult({ error: err?.message || String(err) });
      showAlert('Failed to run auto-reschedule: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setAutoRescheduleRunning(false);
    }
  }

  async function handleRescheduleByAvailability() {
    setAvailabilityRescheduleRunning(true);
    setAvailabilityRescheduleResult(null);
    try {
      const data = await rescheduleUnschedulableByAvailability({ gameId: adminGameId });
      setAvailabilityRescheduleResult(data);
      const skipMsg = data.skippedTournaments?.length ? ` Skipped: ${data.skippedTournaments.join(', ')}.` : '';
      showAlert(
        `Scheduled ${data.scheduled ?? 0} match(es) by availability.${data.stillUnschedulable > 0 ? ` ${data.stillUnschedulable} still need scheduling (no mutual slot).` : ''}${skipMsg}${data.errors?.length ? ` Errors: ${data.errors.length}` : ''}`,
        { title: 'Reschedule by availability', variant: 'info' }
      );
    } catch (err) {
      console.error('[TournamentSchedule] handleRescheduleByAvailability failed', err?.message, err);
      setAvailabilityRescheduleResult({ error: err?.message || String(err) });
      showAlert('Failed to reschedule by availability: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setAvailabilityRescheduleRunning(false);
    }
  }

  return (
    <div className="admin-panel-container">
      <header className="admin-header">
        <div>
          <h1 className="page-title">Tournament schedule management</h1>
          <p className="page-subtitle">
            Reschedule matches in chronological order with no double-booked slots. Game: {adminGameId || 'table-tennis'} (switch in header)
          </p>
        </div>
        <div className="admin-header-actions">
          <button type="button" onClick={() => navigate('/tournaments')} className="btn-secondary">
            Tournaments
          </button>
          <button type="button" onClick={() => navigate('/tournament/manage')} className="btn-secondary">
            Tournament management
          </button>
        </div>
      </header>

      <div className="admin-content">
        <div className="admin-section card-ui">
          <h2>Schedule</h2>
          <div style={{ marginTop: '16px' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px' }}>Auto-reschedule matches</h3>
            <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '14px' }}>
              Reschedule matches in chronological order with no double-booked slots. <strong>All tournaments must have at least one venue and time slots</strong> (weekdays, start/end time); tournaments without venue or schedule are skipped.
            </p>
            <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <input
                type="checkbox"
                checked={useBufferTime}
                onChange={(e) => setUseBufferTime(e.target.checked)}
              />
              <span>Use buffer time (1 hr earlier, 30 min later than tournament schedule)</span>
            </label>
            <p style={{ margin: '0 0 16px 0', color: '#666', fontSize: '13px' }}>
              {useBufferTime ? 'Slots include an extended window around the defined schedule.' : 'Slots use only the actual tournament schedule (no buffer).'}
              For tournaments with &quot;Ask players to suggest availability&quot; enabled, only slots where all players are available are used.
            </p>
            <button
              type="button"
              onClick={handleRunAutoReschedule}
              disabled={autoRescheduleRunning}
              className="btn-primary"
            >
              {autoRescheduleRunning ? 'Running…' : 'Reschedule matches (fix conflicts, chronological order)'}
            </button>
            {autoRescheduleResult && (
              <p style={{ margin: '12px 0 0', color: autoRescheduleResult.error ? '#c00' : '#333', fontSize: '14px' }}>
                {autoRescheduleResult.error || `Rescheduled ${autoRescheduleResult.rescheduled ?? 0} match(es).`}
                {autoRescheduleResult.skippedTournaments?.length ? ` Skipped: ${autoRescheduleResult.skippedTournaments.join(', ')}.` : ''}
              </p>
            )}
          </div>

          <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #dee2e6' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px' }}>Reschedule by availability</h3>
            <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '14px' }}>
              For tournaments with &quot;Ask players to suggest availability&quot; enabled: (1) schedules unschedulable matches into mutual time slots; (2) reschedules matches that are currently on days or times when a player is unavailable (exclusion/holiday or wrong slot). Tries swapping match slots when possible. Players must have submitted their availability first.
            </p>
            <button
              type="button"
              onClick={handleRescheduleByAvailability}
              disabled={availabilityRescheduleRunning}
              className="btn-primary"
            >
              {availabilityRescheduleRunning ? 'Running…' : 'Reschedule unschedulable by availability'}
            </button>
            {availabilityRescheduleResult && (
              <p style={{ margin: '12px 0 0', color: availabilityRescheduleResult.error ? '#c00' : '#333', fontSize: '14px' }}>
                {availabilityRescheduleResult.error || `Scheduled ${availabilityRescheduleResult.scheduled ?? 0} match(es).`}
                {availabilityRescheduleResult.stillUnschedulable > 0 && ` ${availabilityRescheduleResult.stillUnschedulable} still unschedulable.`}
                {availabilityRescheduleResult.skippedTournaments?.length ? ` Skipped: ${availabilityRescheduleResult.skippedTournaments.join(', ')}.` : ''}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
